/**
 * ImportsQueuePage — `/admin/imports` list surface.
 *
 * Matches the design artboard
 * (`.design-incoming/admin-20260522/source/admin.jsx` ImportsPage):
 *
 *   - Header: eyebrow "INTERNAL ADMIN" + H1 "Imports" + subtitle
 *     "Push CSVs into client systems. {N} active." + "+ New import"
 *     (gated on `imports:run`, opens the New Import sheet).
 *   - A single `Card` of card-rows (NO filter chips, NO search, NO
 *     column headers): ImportStatusDot + company name + mono import id
 *     (+ red error line) | type | "{N} rows" | status pill | initiator
 *     email | chevron. Whole-row click → detail.
 *
 * Earlier this surface carried Status/Type/Date filter chips, a search
 * box, a summary-count subtitle, a header table, in-row progress bars,
 * and live polling — all built to `IMPORTS_SPEC.md`, which had drifted
 * from the artboard. The artboard + reviewed screenshot are the source
 * of truth, so the list is rebuilt to the simple card-row form.
 *
 * SCRUM-15 de-scope ratification (Miguel, 2026-05-27 — recorded in
 * docs/decisions/scrum-15-admin-surface-descope.md): those five
 * capabilities (filter chips · search · summary counts · per-row
 * progress · live polling/auto-refresh) are INTENTIONALLY dropped, not
 * accidental collateral of the rebuild. They return only in their own
 * ticket if the queue grows enough to warrant them. The New Import
 * sheet wiring (SCRUM-126) is kept.
 *
 * `ProgressBar` is still exported for `ImportDetailPage` (the detail
 * page shows progress; the list does not).
 *
 * Phase-1: `useImports()` returns `[]` → "No imports yet" empty state.
 * Tests drive the populated list via `importsOverride`.
 */

import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import {
  ImportStatusDot,
  type ImportStatus,
} from '../_components/ImportStatusDot.js';
import { type ImportType } from '../_components/ImportTypeChip.js';
import { useCompactMode } from '../../../shared/hooks/useCompactMode.js';
import {
  NewImportSheet,
  type NewImportCompanyOption,
  type NewImportSubmitPayload,
} from '../_components/NewImportSheet.js';

// ── Types ──────────────────────────────────────────────────────

export interface ImportRow {
  readonly id: string;
  readonly type: ImportType;
  readonly company_id: string;
  readonly company_name: string;
  readonly company_slug: string;
  readonly status: ImportStatus;
  readonly rows_total: number;
  readonly rows_processed: number;
  /** Row-level error count (used by the detail page). */
  readonly error_count: number;
  /** Short human error line shown under failed rows ("Invalid email on row 8"). */
  readonly error_message?: string;
  readonly initiator_email: string;
  readonly created_at: string;
  readonly queue_position?: number;
}

// ── Phase-1 stub data hook ─────────────────────────────────────

function useImports(): readonly ImportRow[] {
  return React.useMemo<readonly ImportRow[]>(() => [], []);
}

// ── Status pill ────────────────────────────────────────────────

const STATUS_VISUAL: Record<
  ImportStatus,
  { label: string; background: string; foreground: string; border: string }
> = {
  queued: {
    label: 'queued',
    background: 'var(--bg-sunken)',
    foreground: 'var(--fg-secondary)',
    border: 'var(--border-default)',
  },
  running: {
    label: 'running',
    background: 'color-mix(in oklab, var(--brand-500) 12%, var(--bg-surface))',
    foreground: 'var(--brand-700)',
    border: 'color-mix(in oklab, var(--brand-500) 30%, transparent)',
  },
  completed: {
    label: 'completed',
    background:
      'color-mix(in oklab, var(--success-500) 14%, var(--bg-surface))',
    foreground: 'var(--success-700)',
    border: 'color-mix(in oklab, var(--success-500) 32%, transparent)',
  },
  completed_with_errors: {
    label: 'completed · errors',
    background:
      'color-mix(in oklab, var(--warning-500) 14%, var(--bg-surface))',
    foreground: 'var(--warning-700)',
    border: 'color-mix(in oklab, var(--warning-500) 32%, transparent)',
  },
  failed: {
    label: 'failed',
    background: 'color-mix(in oklab, var(--danger-500) 12%, var(--bg-surface))',
    foreground: 'var(--danger-700)',
    border: 'color-mix(in oklab, var(--danger-500) 30%, transparent)',
  },
};

function ImportStatusPill({
  status,
}: {
  readonly status: ImportStatus;
}): React.ReactElement {
  const v = STATUS_VISUAL[status];
  return (
    <span
      className="inline-flex items-center font-medium"
      style={{
        padding: '2px 10px',
        borderRadius: 'var(--radius-full)',
        background: v.background,
        color: v.foreground,
        border: `1px solid ${v.border}`,
        fontSize: '12px',
        whiteSpace: 'nowrap',
      }}
    >
      {v.label}
    </span>
  );
}

// ── Progress bar (exported for ImportDetailPage) ───────────────

export function ProgressBar({
  rowsProcessed,
  rowsTotal,
}: {
  readonly rowsProcessed: number;
  readonly rowsTotal: number;
}): React.ReactElement {
  const pct =
    rowsTotal <= 0
      ? 0
      : Math.max(0, Math.min(100, (rowsProcessed / rowsTotal) * 100));
  return (
    <div
      role="progressbar"
      aria-label={`Import progress: ${rowsProcessed} of ${rowsTotal} rows`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      style={{
        width: '100%',
        height: '3px',
        background: 'var(--bg-sunken)',
        borderRadius: 'var(--radius-full)',
        overflow: 'hidden',
        marginTop: '2px',
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: `${pct}%`,
          height: '100%',
          background: 'var(--brand-500)',
          transition: 'width 200ms ease',
        }}
      />
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────

export interface ImportsQueuePageProps {
  /** Test injection for `useImports`. Production unset. */
  readonly importsOverride?: ReadonlyArray<ImportRow>;
  readonly compact?: boolean;
  /**
   * Test injection for the new-import sheet's company typeahead.
   * Phase-1 default `[]` until the BE ships a company-index endpoint.
   */
  readonly newImportCompanyOptions?: ReadonlyArray<NewImportCompanyOption>;
}

export function ImportsQueuePage(
  props: ImportsQueuePageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission permission="imports:read" fallback={<NotPermittedPanel />}>
      <ImportsQueueBody {...props} />
    </IfAdminPermission>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <section
      aria-labelledby="imports-not-permitted-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="imports-not-permitted-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Not permitted
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        You don't have access to view Imports. Ask a platform administrator
        if you need this access.
      </p>
    </section>
  );
}

// ── Body ───────────────────────────────────────────────────────

function ImportsQueueBody({
  importsOverride,
  compact: compactOverride,
  newImportCompanyOptions,
}: ImportsQueuePageProps): React.ReactElement {
  const compact = useCompactMode(compactOverride);
  const navigate = useNavigate();
  const remote = useImports();
  const rows = importsOverride ?? remote;

  // SCRUM-126: + New import sheet open/close state lives at the page
  // level so both the header CTA and the empty-state CTA open it. On
  // submit, route to /admin/imports?created=<stub-id> (Phase-1 stub
  // until POST /admin/imports ships).
  const [newImportOpen, setNewImportOpen] = React.useState(false);
  const handleNewImportSubmit = (payload: NewImportSubmitPayload): void => {
    setNewImportOpen(false);
    const stubId = `imp_pending_${Date.now()}`;
    navigate(
      `/admin/imports?created=${encodeURIComponent(stubId)}&type=${encodeURIComponent(payload.type)}`,
    );
  };

  const activeCount = rows.filter(
    (r) => r.status === 'running' || r.status === 'queued',
  ).length;

  return (
    <section
      aria-labelledby="imports-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '1280px', padding: compact ? '24px 16px 80px' : '32px 48px 80px' }}
    >
      {/* Header */}
      <div
        className="flex flex-wrap items-end justify-between"
        style={{ gap: '16px', marginBottom: '24px' }}
      >
        <div>
          <p
            className="m-0 font-bold uppercase"
            style={{
              fontSize: '11px',
              letterSpacing: '1.2px',
              color: 'var(--fg-tertiary)',
              marginBottom: '6px',
            }}
          >
            Internal admin
          </p>
          <h1
            id="imports-heading"
            className="m-0 font-semibold"
            style={{
              fontSize: '28px',
              letterSpacing: '-0.4px',
              color: 'var(--fg-primary)',
            }}
          >
            Imports
          </h1>
          <p
            className="m-0"
            style={{
              fontSize: '14px',
              color: 'var(--fg-secondary)',
              marginTop: '6px',
            }}
          >
            Push CSVs into client systems. {activeCount} active.
          </p>
        </div>
        <IfAdminPermission permission="imports:run">
          <Button
            type="button"
            variant="primary"
            onClick={() => setNewImportOpen(true)}
          >
            + New import
          </Button>
        </IfAdminPermission>
      </div>

      {/* List */}
      {rows.length === 0 ? (
        <EmptyState
          onNewImport={() => setNewImportOpen(true)}
        />
      ) : compact ? (
        <div className="flex flex-col" style={{ gap: '8px' }}>
          {rows.map((row) => (
            <ImportCardRow key={row.id} row={row} compact />
          ))}
        </div>
      ) : (
        <Card padding="none">
          {rows.map((row, idx) => (
            <ImportCardRow key={row.id} row={row} first={idx === 0} />
          ))}
        </Card>
      )}

      {/* New-import sheet (mounted at page level). */}
      <NewImportSheet
        open={newImportOpen}
        onCancel={() => setNewImportOpen(false)}
        onSubmit={handleNewImportSubmit}
        companyOptions={newImportCompanyOptions}
      />
    </section>
  );
}

function EmptyState({
  onNewImport,
}: {
  readonly onNewImport: () => void;
}): React.ReactElement {
  return (
    <Card padding="lg">
      <p
        className="m-0 text-center"
        style={{ fontSize: '14px', color: 'var(--fg-secondary)' }}
      >
        No imports yet. Run your first.
      </p>
      <IfAdminPermission permission="imports:run">
        <div className="mt-3 flex justify-center" style={{ marginTop: '12px' }}>
          <Button type="button" variant="primary" onClick={onNewImport}>
            + New import
          </Button>
        </div>
      </IfAdminPermission>
    </Card>
  );
}

// ── Card row ───────────────────────────────────────────────────

function ImportCardRow({
  row,
  first,
  compact,
}: {
  readonly row: ImportRow;
  readonly first?: boolean;
  readonly compact?: boolean;
}): React.ReactElement {
  const navigate = useNavigate();
  const href = `/admin/imports/${row.id}`;
  const handleClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a, button, [role="link"], [role="button"]') != null) {
      return;
    }
    navigate(href);
  };
  const typeLabel = row.type.replace(/_/g, ' ');

  return (
    <div
      data-testid={`import-row-${row.id}`}
      onClick={handleClick}
      className="grid cursor-pointer items-center hover:bg-[var(--bg-sunken)]"
      style={{
        gridTemplateColumns: compact ? '1fr auto' : '1fr 150px 100px 150px 160px 32px',
        padding: '14px 20px',
        gap: '12px',
        borderTop:
          compact || first ? 'none' : '1px solid var(--border-subtle)',
        ...(compact
          ? { border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)' }
          : {}),
      }}
    >
      {/* Company block */}
      <div className="flex min-w-0 items-center" style={{ gap: '10px' }}>
        <ImportStatusDot status={row.status} />
        <div className="min-w-0">
          <Link
            to={href}
            aria-label={`Open import for ${row.company_name}`}
            className="block truncate font-semibold no-underline"
            style={{ fontSize: '14px', color: 'var(--fg-primary)' }}
          >
            {row.company_name}
          </Link>
          <span
            className="block truncate font-mono"
            style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
          >
            {row.id}
          </span>
          {row.error_message !== undefined ? (
            <span
              className="block truncate"
              style={{ fontSize: '12px', color: 'var(--fg-danger)', marginTop: '2px' }}
            >
              {row.error_message}
            </span>
          ) : null}
        </div>
      </div>

      {/* Compact: status pill only, on the right. Desktop: full columns. */}
      {compact ? (
        <ImportStatusPill status={row.status} />
      ) : (
        <>
          <span style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}>
            {typeLabel}
          </span>
          <span style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}>
            {row.rows_total.toLocaleString()} rows
          </span>
          <ImportStatusPill status={row.status} />
          <span
            className="truncate font-mono"
            style={{ fontSize: '12px', color: 'var(--fg-tertiary)' }}
          >
            {row.initiator_email}
          </span>
          <Icon name="chevRight" size={16} aria-hidden />
        </>
      )}
    </div>
  );
}
